import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AutoRefreshCountdown from '../common/AutoRefreshCountdown';
import { withUnit } from '../../utils/formatUnits';

// Live dashboard of every production line: telemetry from the PLC / diameter gauge
// (line_telemetry_readings), current item, stoppages. Data arrives from the machine
// integration — until it is connected the cards show "no data".

type LineStatus = 'producing' | 'no_diameter' | 'stopped' | 'no_data';

interface CurrentItem {
  production_item_id: number;
  material: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  kolor: string | null;
  srednica: string | null;
  waga: string | null;
  indeks_kat: string | null;
  do_produkcji: number;
  produced: number;
  started_at: string | null;
}

interface LineSnapshot {
  line_id: number;
  line_name: string;
  external_code: string | null;
  description: string | null;
  ip_address: string | null;
  has_telemetry: boolean;
  status: LineStatus;
  last_reading_at: string | null;
  reading_age_seconds: number | null;
  speed_m_min: number | null;
  avg_speed_3h_m_min: number | null;
  avg_samples: number;
  diameter_x_mm: number | null;
  diameter_y_mm: number | null;
  diameter_xy_mm: number | null;
  // Dual-strand lines: speed_m_min / diameter_x_mm / diameter_y_mm above are strand A.
  is_dual_strand: boolean;
  speed_b_m_min: number | null;
  avg_speed_b_3h_m_min: number | null;
  diameter_b_x_mm: number | null;
  diameter_b_y_mm: number | null;
  filament_lost_strands: ('A' | 'B')[];
  // Strands actually running: one on a dual-strand machine that winds a single filament.
  strands_in_use: ('A' | 'B')[];
  melt_temperature_c: number | null;
  pressure_bar: number | null;
  screw_rpm: number | null;
  screw_load: number | null;
  stopped_since: string | null;
  stopped_for_seconds: number | null;
  stoppage_reason: 'no_speed' | 'filament_lost' | null;
  stoppage_notes: string | null;
  today_stoppages_count: number;
  today_stopped_seconds: number;
  current_item: CurrentItem | null;
}

interface Kpi {
  lines_total: number;
  lines_producing: number;
  lines_stopped: number;
  lines_no_diameter: number;
  lines_no_data: number;
  total_speed_m_min: number;
  today_pcs: number;
  today_kg: number;
}

interface Stoppage {
  id: number;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number;
  open: boolean;
  source: string;
  reason: 'no_speed' | 'filament_lost' | null;
  notes: string | null;
  /** Reported by the operator in the "line has been standing" message (STOPPAGE_REASONS). */
  operator_reason: string | null;
  operator_note: string | null;
  reported_by: string | null;
}

const STATUS_COLORS: Record<LineStatus, { dot: string; bg: string; border: string }> = {
  producing: { dot: '#10b981', bg: '#ecfdf5', border: '#10b981' },
  // Line runs but the gauge reads no filament — a warning, deliberately NOT a stoppage.
  no_diameter: { dot: '#f59e0b', bg: '#fffbeb', border: '#f59e0b' },
  stopped: { dot: '#dc3545', bg: '#fef2f2', border: '#dc3545' },
  no_data: { dot: '#94a3b8', bg: '#f8fafc', border: '#cbd5e1' },
};

const fmtDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}min`;
  if (m > 0) return `${m}min`;
  return `${Math.floor(seconds)}s`;
};

const fmtTime = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const fmtNum = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined ? '—' : v.toLocaleString('pl-PL', { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Diameter is "correct" within ±0.05 mm of the nominal (1.75 / 2.85) — a plain visual hint.
const diameterTone = (value: number | null, nominal: number | null): string => {
  if (value === null || nominal === null) return '#1e293b';
  return Math.abs(value - nominal) <= 0.05 ? '#059669' : '#dc3545';
};

const LineDashboard: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState<Kpi | null>(null);
  const [lines, setLines] = useState<LineSnapshot[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string>('');
  const [historyLine, setHistoryLine] = useState<LineSnapshot | null>(null);
  const [stoppages, setStoppages] = useState<{ total_seconds: number; count: number; filament_lost_seconds: number; filament_lost_count: number; stoppages: Stoppage[] } | null>(null);
  const [historyDays, setHistoryDays] = useState(7);

  const fetchAll = useCallback(async () => {
    try {
      const res = await api.get('/admin/line-dashboard');
      setKpi(res.data.kpi);
      setLines(res.data.lines);
      setGeneratedAt(res.data.generated_at);
    } catch {
      // keep last snapshot
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const timer = setInterval(fetchAll, 15_000);
    return () => clearInterval(timer);
  }, [fetchAll]);

  useEffect(() => {
    if (!historyLine) { setStoppages(null); return; }
    let cancelled = false;
    api.get(`/admin/line-dashboard/${historyLine.line_id}/stoppages`, { params: { days: historyDays } })
      .then(res => { if (!cancelled) setStoppages(res.data); })
      .catch(() => { if (!cancelled) setStoppages({ total_seconds: 0, count: 0, filament_lost_seconds: 0, filament_lost_count: 0, stoppages: [] }); });
    return () => { cancelled = true; };
  }, [historyLine, historyDays]);

  const kpiCards = kpi ? [
    { label: t('lineDashboard.kpiActive'), value: `${kpi.lines_producing} / ${kpi.lines_total}`, color: '#10b981', bg: '#ecfdf5' },
    { label: t('lineDashboard.kpiStopped'), value: String(kpi.lines_stopped), color: '#dc3545', bg: '#fef2f2' },
    { label: t('lineDashboard.kpiNoDiameter'), value: String(kpi.lines_no_diameter ?? 0), color: '#d97706', bg: '#fffbeb' },
    { label: t('lineDashboard.kpiTotalSpeed'), value: `${fmtNum(kpi.total_speed_m_min)} m/min`, color: '#2563eb', bg: '#eff6ff' },
    { label: t('lineDashboard.kpiTodayPcs'), value: kpi.today_pcs.toLocaleString('pl-PL'), color: '#667eea', bg: '#eef2ff' },
    { label: t('lineDashboard.kpiTodayKg'), value: `${fmtNum(kpi.today_kg)} kg`, color: '#667eea', bg: '#eef2ff' },
  ] : [];

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>📡 {t('lineDashboard.title')}</h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('lineDashboard.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {generatedAt && <span style={{ fontSize: 12, color: '#94a3b8' }}>{t('lineDashboard.updatedAt')}: {fmtTime(generatedAt)}</span>}
          <AutoRefreshCountdown intervalSeconds={15} onRefresh={fetchAll} />
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {kpiCards.map(({ label, value, color, bg }) => (
          <div key={label} style={{ flex: 1, minWidth: 150, background: bg, border: `1px solid ${color}30`, borderRadius: 10, padding: '14px 20px' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Legend — what each colour means and which ones count as a stoppage */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', marginBottom: 20, padding: '10px 14px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, color: '#475569' }}>
        <span style={{ fontWeight: 700, color: '#1e293b' }}>{t('lineDashboard.legendTitle')}:</span>
        {(['producing', 'no_diameter', 'stopped', 'no_data'] as LineStatus[]).map(st => (
          <span key={st} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: '50%', background: STATUS_COLORS[st].dot, flexShrink: 0 }} />
            <span>{t(`lineDashboard.legend_${st}`)}</span>
          </span>
        ))}
      </div>

      {kpi && kpi.lines_no_data === kpi.lines_total && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#92400e' }}>
          ⚠ {t('lineDashboard.noTelemetryYet')}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>{t('common.loading')}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {lines.map(line => {
            const c = STATUS_COLORS[line.status];
            const item = line.current_item;
            const nominal = item?.srednica ? parseFloat(String(item.srednica).replace(',', '.')) : null;
            const colour = item?.kolor?.includes('\n') ? item.kolor.split('\n').join('/') : item?.kolor;
            return (
              <div key={line.line_id} style={{
                background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${c.border}`,
                borderRadius: 12, padding: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                {/* Title + status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', background: c.dot, boxShadow: line.status === 'producing' ? `0 0 0 4px ${c.dot}33` : 'none', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t('lineDashboard.line')} {line.line_name}
                    </div>
                    {line.description && <div style={{ fontSize: 12, color: '#64748b', whiteSpace: 'pre-line' }}>{line.description}</div>}
                    {line.ip_address && <div style={{ fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' }}>{line.ip_address}</div>}
                  </div>
                  <span style={{ padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, background: c.bg, color: c.dot === '#94a3b8' ? '#64748b' : c.dot, whiteSpace: 'nowrap' }}>
                    {line.status === 'producing' && t('lineDashboard.statusProducing')}
                    {line.status === 'no_diameter' && t('lineDashboard.statusNoDiameter')}
                    {line.status === 'stopped' && t('lineDashboard.statusStopped')}
                    {line.status === 'no_data' && t('lineDashboard.statusNoData')}
                  </span>
                </div>

                {line.status === 'stopped' && (
                  <div style={{ fontSize: 13, color: '#991b1b', background: '#fef2f2', borderRadius: 8, padding: '6px 10px' }}>
                    ⏹ {t('lineDashboard.stoppedFor')}: <strong>{fmtDuration(line.stopped_for_seconds)}</strong> ({t('lineDashboard.since')} {fmtTime(line.stopped_since)})
                  </div>
                )}
                {line.status === 'no_diameter' && (
                  <div style={{ fontSize: 13, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '6px 10px' }}>
                    ⚠ <strong>{t('lineDashboard.filamentLost')}</strong>
                    {line.is_dual_strand && line.filament_lost_strands.length > 0 && (
                      <> ({line.filament_lost_strands.map(s => t(`lineDashboard.strand${s}`)).join(', ')})</>
                    )}
                    {line.stopped_since && (
                      <> — {t('lineDashboard.lastsFor')}: <strong>{fmtDuration(line.stopped_for_seconds)}</strong> ({t('lineDashboard.since')} {fmtTime(line.stopped_since)})</>
                    )}
                  </div>
                )}

                {/* Current item */}
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#475569' }}>
                  {item ? (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 3 }}>
                        <span style={{ color: '#94a3b8' }}>{t('lineDashboard.material')}</span>
                        <strong style={{ color: '#1e293b' }}>{item.material || '—'} {colour ? `· ${colour}` : ''} {item.srednica ? `· ${withUnit(item.srednica, 'mm')}` : ''}</strong>
                        <span style={{ color: '#94a3b8' }}>{t('lineDashboard.brand')}</span>
                        <span>{item.marka || '—'}</span>
                        <span style={{ color: '#94a3b8' }}>{t('lineDashboard.originalName')}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.nazwa_oryg || ''}>{item.nazwa_oryg || '—'}</span>
                        <span style={{ color: '#94a3b8' }}>{t('lineDashboard.progress')}</span>
                        <span><strong>{item.produced}</strong> / {item.do_produkcji} {t('checkpoint.pcs')}</span>
                      </div>
                    </>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>{t('lineDashboard.noCurrentItem')}</span>
                  )}
                </div>

                {/* Telemetry numbers */}
                {line.strands_in_use.length > 1 ? (
                  <>
                    <StrandTable
                      strands={[
                        { key: 'A', speed: line.speed_m_min, avg: line.avg_speed_3h_m_min, x: line.diameter_x_mm, y: line.diameter_y_mm },
                        { key: 'B', speed: line.speed_b_m_min, avg: line.avg_speed_b_3h_m_min, x: line.diameter_b_x_mm, y: line.diameter_b_y_mm },
                      ]}
                      lost={line.filament_lost_strands}
                      nominal={nominal}
                    />
                    <Metric label={t('lineDashboard.todayStopped')} value={fmtDuration(line.today_stopped_seconds)} unit={`(${line.today_stoppages_count}×)`} />
                  </>
                ) : line.strands_in_use[0] === 'B' ? (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#667eea', marginBottom: 6 }}>{t('lineDashboard.strandB')}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8 }}>
                      <Metric label={t('lineDashboard.speedNow')} value={fmtNum(line.speed_b_m_min)} unit="m/min" big />
                      <Metric label={t('lineDashboard.speedAvg3h')} value={fmtNum(line.avg_speed_b_3h_m_min)} unit="m/min" big />
                      <Metric label={t('lineDashboard.diameterX')} value={fmtNum(line.diameter_b_x_mm, 3)} unit="mm" color={diameterTone(line.diameter_b_x_mm, nominal)} />
                      <Metric label={t('lineDashboard.diameterY')} value={fmtNum(line.diameter_b_y_mm, 3)} unit="mm" color={diameterTone(line.diameter_b_y_mm, nominal)} />
                      <Metric label={t('lineDashboard.todayStopped')} value={fmtDuration(line.today_stopped_seconds)} unit={`(${line.today_stoppages_count}×)`} />
                    </div>
                  </div>
                ) : (
                  <div>
                    {line.is_dual_strand && <div style={{ fontSize: 12, fontWeight: 800, color: '#667eea', marginBottom: 6 }}>{t('lineDashboard.strandA')}</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8 }}>
                      <Metric label={t('lineDashboard.speedNow')} value={fmtNum(line.speed_m_min)} unit="m/min" big />
                      <Metric label={t('lineDashboard.speedAvg3h')} value={fmtNum(line.avg_speed_3h_m_min)} unit="m/min" big />
                      <Metric label={t('lineDashboard.diameterX')} value={fmtNum(line.diameter_x_mm, 3)} unit="mm" color={diameterTone(line.diameter_x_mm, nominal)} />
                      <Metric label={t('lineDashboard.diameterY')} value={fmtNum(line.diameter_y_mm, 3)} unit="mm" color={diameterTone(line.diameter_y_mm, nominal)} />
                      {!line.is_dual_strand && <Metric label={t('lineDashboard.diameterXY')} value={fmtNum(line.diameter_xy_mm, 3)} unit="mm" color={diameterTone(line.diameter_xy_mm, nominal)} />}
                      <Metric label={t('lineDashboard.todayStopped')} value={fmtDuration(line.today_stopped_seconds)} unit={`(${line.today_stoppages_count}×)`} />
                    </div>
                  </div>
                )}

                {/* Extruder process values — only for lines whose PLC publishes them */}
                {[line.melt_temperature_c, line.pressure_bar, line.screw_rpm, line.screw_load].some(v => v !== null) && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: '#667eea', marginBottom: 6 }}>{t('lineDashboard.extruder')}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 8 }}>
                      <Metric label={t('lineDashboard.meltTemperature')} value={fmtNum(line.melt_temperature_c)} unit="°C" />
                      <Metric label={t('lineDashboard.pressure')} value={fmtNum(line.pressure_bar, line.pressure_bar !== null && !Number.isInteger(line.pressure_bar) ? 2 : 0)} unit="bar" />
                      <Metric label={t('lineDashboard.screwRpm')} value={fmtNum(line.screw_rpm, 0)} unit="rpm" />
                      <Metric label={t('lineDashboard.screwLoad')} value={fmtNum(line.screw_load, 0)} />
                    </div>
                  </div>
                )}

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', gap: 8, marginTop: 'auto' }}>
                  <span>{t('lineDashboard.lastReading')}: {line.last_reading_at ? fmtTime(line.last_reading_at) : '—'}</span>
                  <button
                    onClick={() => setHistoryLine(line)}
                    style={{ flexShrink: 0, whiteSpace: 'nowrap', padding: '6px 12px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer' }}
                  >
                    📋 {t('lineDashboard.stoppageHistory')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Stoppage history modal */}
      {historyLine && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, maxWidth: 640, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 20, color: '#1e293b', flex: 1 }}>
                ⏹ {t('lineDashboard.stoppageHistory')} — {t('lineDashboard.line')} {historyLine.line_name}{historyLine.external_code ? ` (${historyLine.external_code})` : ''}
              </h2>
              <select value={historyDays} onChange={e => setHistoryDays(parseInt(e.target.value, 10))} style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}>
                {[1, 7, 14, 30].map(d => <option key={d} value={d}>{t('lineDashboard.lastDays', { days: d })}</option>)}
              </select>
              <button onClick={() => setHistoryLine(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', fontWeight: 700, color: '#475569' }}>✕</button>
            </div>
            {stoppages ? (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div style={{ flex: 1, background: '#fef2f2', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#dc3545' }}>{fmtDuration(stoppages.total_seconds)}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{t('lineDashboard.totalStopped')}</div>
                  </div>
                  <div style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{stoppages.count}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{t('lineDashboard.stoppageCount')}</div>
                  </div>
                  <div style={{ flex: 1, background: '#fffbeb', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#92400e' }}>{fmtDuration(stoppages.filament_lost_seconds)} <span style={{ fontSize: 14, fontWeight: 700 }}>({stoppages.filament_lost_count}×)</span></div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{t('lineDashboard.filamentLostTotal')}</div>
                  </div>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f8f9fa' }}>
                        <th style={th}>{t('lineDashboard.colStart')}</th>
                        <th style={th}>{t('lineDashboard.colEnd')}</th>
                        <th style={th}>{t('lineDashboard.colDuration')}</th>
                        <th style={th}>{t('lineDashboard.colReason')}</th>
                        <th style={th}>{t('lineDashboard.colOperatorReason')}</th>
                        <th style={th}>{t('lineDashboard.colSource')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stoppages.stoppages.length === 0 ? (
                        <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 24 }}>{t('lineDashboard.noStoppages')}</td></tr>
                      ) : stoppages.stoppages.map(s => (
                        <tr key={s.id} style={{ background: s.open ? '#fef2f2' : '#fff' }}>
                          <td style={td}>{fmtTime(s.started_at)}</td>
                          <td style={td}>{s.open ? <strong style={{ color: '#dc3545' }}>{t('lineDashboard.stillStopped')}</strong> : fmtTime(s.ended_at)}</td>
                          <td style={{ ...td, fontWeight: 700 }}>{fmtDuration(s.duration_seconds)}</td>
                          <td style={td}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: s.reason === 'filament_lost' ? '#fef3c7' : '#fee2e2', color: s.reason === 'filament_lost' ? '#92400e' : '#991b1b' }}>
                              {s.reason === 'filament_lost' ? t('lineDashboard.filamentLost') : t('lineDashboard.statusStopped')}
                            </span>
                            {s.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.notes}</div>}
                          </td>
                          <td style={td}>
                            {s.operator_reason ? (
                              <>
                                <strong>{t(`stoppageAlert.reasons.${s.operator_reason}`)}</strong>
                                {s.operator_note && <div style={{ fontSize: 11, color: '#334155', marginTop: 2 }}>{s.operator_note}</div>}
                                {s.reported_by && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{s.reported_by}</div>}
                              </>
                            ) : <span style={{ color: '#94a3b8' }}>{s.reason === 'filament_lost' ? '—' : t('lineDashboard.operatorReasonMissing')}</span>}
                          </td>
                          <td style={td}>{s.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>{t('common.loading')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', fontSize: 11, color: '#6c757d', borderBottom: '2px solid #dee2e6', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #f0f0f0' };

const Metric: React.FC<{ label: string; value: string; unit?: string; big?: boolean; color?: string }> = ({ label, value, unit, big, color }) => (
  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px' }}>
    <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    <div style={{ fontSize: big ? 22 : 17, fontWeight: 800, color: color ?? '#1e293b', lineHeight: 1.2 }}>
      {value} {unit && <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>{unit}</span>}
    </div>
  </div>
);

type StrandValues = { key: 'A' | 'B'; speed: number | null; avg: number | null; x: number | null; y: number | null };

// Dual-strand telemetry as one compact A | B table instead of two stacked tile columns.
const StrandTable: React.FC<{ strands: StrandValues[]; lost: ('A' | 'B')[]; nominal: number | null }> = ({ strands, lost, nominal }) => {
  const { t } = useLanguage();
  const rows: { label: string; render: (s: StrandValues) => React.ReactNode }[] = [
    { label: t('lineDashboard.speedNow'), render: s => <Value value={fmtNum(s.speed)} unit="m/min" big /> },
    { label: t('lineDashboard.speedAvg3h'), render: s => <Value value={fmtNum(s.avg)} unit="m/min" /> },
    { label: t('lineDashboard.diameterX'), render: s => <Value value={fmtNum(s.x, 2)} unit="mm" color={diameterTone(s.x, nominal)} /> },
    { label: t('lineDashboard.diameterY'), render: s => <Value value={fmtNum(s.y, 2)} unit="mm" color={diameterTone(s.y, nominal)} /> },
  ];
  const cellBg = (s: StrandValues) => (lost.includes(s.key) ? '#fffbeb' : '#fff');
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '34%' }} />
          <col style={{ width: '33%' }} />
          <col style={{ width: '33%' }} />
        </colgroup>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={strandTh} />
            {strands.map(s => (
              <th key={s.key} style={{ ...strandTh, color: lost.includes(s.key) ? '#92400e' : '#667eea', background: lost.includes(s.key) ? '#fef3c7' : undefined }}>
                {t(`lineDashboard.strand${s.key}`)}{lost.includes(s.key) && ' ⚠'}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.label}>
              <td style={{ ...strandTd, fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.label}>{row.label}</td>
              {strands.map(s => (
                <td key={s.key} style={{ ...strandTd, background: cellBg(s) }}>{row.render(s)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const strandTh: React.CSSProperties = { padding: '6px 10px', textAlign: 'left', fontSize: 12, fontWeight: 800, borderBottom: '1px solid #e2e8f0' };
const strandTd: React.CSSProperties = { padding: '6px 10px', borderBottom: '1px solid #f1f5f9' };

const Value: React.FC<{ value: string; unit?: string; big?: boolean; color?: string }> = ({ value, unit, big, color }) => (
  <span style={{ fontSize: big ? 18 : 15, fontWeight: 800, color: color ?? '#1e293b', whiteSpace: 'nowrap' }}>
    {value} {unit && <span style={{ fontSize: 11, fontWeight: 500, color: '#94a3b8' }}>{unit}</span>}
  </span>
);

export default LineDashboard;
